/**
 * Government typing-exam presets (SSC / RRB / court, TCS iON style).
 *
 * UI-only data for the /govt-typing-test experience. No scoring logic lives here —
 * these presets drive the exam-selection grid, the config modal defaults, and the
 * labels shown on the exam + results screens.
 */

export type ExamDuration = 5 | 10 | 15 | 30;

export interface ExamPreset {
  /** URL-safe id used for selection state. */
  id: string;
  /** Full display name, e.g. "SSC CGL English Typing Test (10 Min)". */
  name: string;
  /** Short label for compact chips, e.g. "SSC CGL". */
  shortName: string;
  /** Recruiting body / authority. */
  organisation: string;
  /** Qualifying net-WPM target. */
  targetWpm: number;
  /** Default test duration in minutes. */
  durationMin: ExamDuration;
  /** Passage language / script. */
  language: string;
  /** One-line description for the card. */
  blurb: string;
  /** Emoji used as a lightweight card icon. */
  icon: string;
}

export const EXAM_PRESETS: ExamPreset[] = [
  {
    id: 'ssc-cgl-english-10',
    name: 'SSC CGL English Typing Test (10 Min)',
    shortName: 'SSC CGL',
    organisation: 'Staff Selection Commission',
    targetWpm: 35,
    durationMin: 10,
    language: 'English',
    blurb: 'Tax Assistant / DEO qualifying test on the TCS iON interface.',
    icon: '📝',
  },
  {
    id: 'ssc-cgl-english-15',
    name: 'SSC CGL English Typing Test (15 Min)',
    shortName: 'SSC CGL',
    organisation: 'Staff Selection Commission',
    targetWpm: 35,
    durationMin: 15,
    language: 'English',
    blurb: 'Extended 15-minute variant used for select CGL posts.',
    icon: '📝',
  },
  {
    id: 'ssc-chsl-english-15',
    name: 'SSC CHSL Typing Test Online',
    shortName: 'SSC CHSL',
    organisation: 'Staff Selection Commission',
    targetWpm: 35,
    durationMin: 15,
    language: 'English',
    blurb: 'LDC / JSA skill test — 35 WPM net over 15 minutes.',
    icon: '🖋️',
  },
  {
    id: 'rrb-ntpc-english-10',
    name: 'RRB NTPC English Typing Test',
    shortName: 'RRB NTPC',
    organisation: 'Railway Recruitment Board',
    targetWpm: 30,
    durationMin: 10,
    language: 'English',
    blurb: 'Junior Clerk / Typist skill test at 30 WPM.',
    icon: '🚆',
  },
  {
    id: 'ssc-steno-english-10',
    name: 'SSC Stenographer Typing Test',
    shortName: 'SSC Steno',
    organisation: 'Staff Selection Commission',
    targetWpm: 80,
    durationMin: 10,
    language: 'English',
    blurb: 'High-speed stenographer transcription practice at 80 WPM.',
    icon: '⚡',
  },
  {
    id: 'allahabad-hc-english-10',
    name: 'Allahabad High Court Typing Test',
    shortName: 'Allahabad HC',
    organisation: 'Allahabad High Court',
    targetWpm: 30,
    durationMin: 10,
    language: 'English',
    blurb: 'Assistant / clerk recruitment typing round.',
    icon: '⚖️',
  },
  {
    id: 'crpf-hc-english-10',
    name: 'CRPF HC Ministerial Typing Test',
    shortName: 'CRPF HC',
    organisation: 'Central Reserve Police Force',
    targetWpm: 35,
    durationMin: 10,
    language: 'English',
    blurb: 'Head Constable (Ministerial) English typing test.',
    icon: '🛡️',
  },
  {
    id: 'dsssb-english-10',
    name: 'DSSSB Typing Test Online',
    shortName: 'DSSSB',
    organisation: 'Delhi Subordinate Services Board',
    targetWpm: 35,
    durationMin: 10,
    language: 'English',
    blurb: 'DSSSB LDC / DEO English typing skill test.',
    icon: '🏛️',
  },
];

export const DURATION_OPTIONS: ExamDuration[] = [5, 10, 15, 30];

/** Config toggles shown in the Test Settings modal (labels + helper copy). */
export interface ExamToggle {
  id: 'backspaceLock' | 'wordHighlight' | 'blindMode' | 'autoScroll';
  label: string;
  helper: string;
  /** Default on/off, mirroring the reference TCS iON behaviour. */
  defaultOn: boolean;
}

export const EXAM_TOGGLES: ExamToggle[] = [
  {
    id: 'backspaceLock',
    label: 'Backspace Lock',
    helper: 'Prevents deletion — simulates strict exam mode.',
    defaultOn: false,
  },
  {
    id: 'wordHighlight',
    label: 'Word Highlight',
    helper: 'Highlights the current word in the passage.',
    defaultOn: true,
  },
  {
    id: 'blindMode',
    label: 'Blind Mode',
    helper: 'Hides errors during the test — exactly like TCS iON.',
    defaultOn: true,
  },
  {
    id: 'autoScroll',
    label: 'Auto-scroll Passage',
    helper: 'Keeps the current word in view as you type.',
    defaultOn: true,
  },
];

/** Sample passage used across the exam + results UI (static demo content). */
export const SAMPLE_PASSAGE =
  'The dowry system has been one of the most persistent social evils in our society. ' +
  'Despite being legally prohibited, the practice continues in many parts of the country, ' +
  'placing an unfair burden on families and undermining the dignity of women. ' +
  'Education and awareness remain the most powerful tools to bring about lasting change. ' +
  'When citizens understand their rights and responsibilities, they are better equipped ' +
  'to reject harmful traditions and build a more equal and just community for everyone.';

export const SAMPLE_TOPIC = 'Social Issues: Dowry System';
export const SAMPLE_DIFFICULTY = 'Medium';

export function getExamById(id: string): ExamPreset | undefined {
  return EXAM_PRESETS.find((e) => e.id === id);
}
